import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Icon } from 'react-icons-kit';
import { eye } from 'react-icons-kit/feather/eye';
import { eyeOff } from 'react-icons-kit/feather/eyeOff';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setLoading] = useState(true);

  const token = searchParams.get('token');
  const baseUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    // Validate the token when the component loads
    console.log('Token:', token);
    const validateToken = async () => {
      setLoading(true);

      if (!token) {
        // If the token is not available, do not proceed
        setError('Invalid or expired token. Please request a new password reset.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/validate-reset-token`, {
          params: { token },
        });
        setIsTokenValid(true);
        setError('');
      } catch (error) {
        setError('Invalid or expired token. Please request a new password reset.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await axios.post(`${baseUrl}/api/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccessMessage('Your password has been reset successfully. You can now log in.');
      setError('');
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setError("Your request period has timed out. Please visit the Forgot Password page to request a new reset link."); 
      } else {
        setError('Failed to reset password. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <p>Loading...</p>; 
  }

  if (!isTokenValid) {
    return <p className="text-red-600">Invalid or expired token.</p>;
  }

  return (
    <div
      className="flex flex-col justify-center items-center min-h-screen p-8 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/images/leelo_login.jpg')" }}
    >
      <div className="bg-gradient-to-br from-white/95 to-uranian-blue/80 backdrop-blur-sm shadow-xl rounded-lg w-full max-w-md p-16 my-auto border border-white/20">
        <h1 className="text-2xl font-bold mb-4 text-tropical-indigo">Enter Your New Password Below</h1>
        {error && <p className="text-red-600 text-sm mt-2 p-2">{error}</p>}
        {successMessage && <p className="text-green-600 text-sm mt-2 p-2">{successMessage}</p>}
        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Desktop version */}
          <div className="relative mb-4 hidden sm:block">
            <label className="block text-gray-700">New Password</label>
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 pr-10 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-[50%] -translate-y-1/2 text-dark-gray bg-transparent border-none cursor-pointer"
            >
              <Icon icon={showNewPassword ? eyeOff : eye} size={20} />
            </button>
          </div>
          <div className="relative mb-4 hidden sm:block">
            <label className="block text-gray-700">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 pr-10 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-[50%] -translate-y-1/2 text-dark-gray bg-transparent border-none cursor-pointer"
            >
              <Icon icon={showConfirmPassword ? eyeOff : eye} size={20} />
            </button>
          </div>

          {/* Mobile version */}
          <div className="relative mb-4 sm:hidden">
            <label className="block text-gray-700">New Password</label>
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 pr-10 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray bg-transparent border-none cursor-pointer"
            >
              <Icon icon={showNewPassword ? eyeOff : eye} size={20} />
            </button>
          </div>
          <div className="relative mb-4 sm:hidden">
            <label className="block text-gray-700">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 pr-10 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray bg-transparent border-none cursor-pointer"
            >
              <Icon icon={showConfirmPassword ? eyeOff : eye} size={20} />
            </button>
          </div>
          <button
            type="submit"
            className="w-full bg-tropical-indigo text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            Reset Password
          </button>
        </form>
        <p className="mt-4 text-sm">
          Return to Forgot Password page?{" "}
          <Link to="/forgot-password" className="text-tropical-indigo">
            Forgot Password
          </Link>
        </p>
        <p className="mt-4 text-sm">
          Return to login page?{" "}
          <Link to="/login" className="text-tropical-indigo">
            Login
          </Link>
        </p>
      </div>
      <footer className="text-white text-xs text-center fixed bottom-4 w-full z-10">
        2025 © Wenzi, Inc. <span className="mx-2">•</span>{" "}
        <Link to="/privacy" className="text-white no-underline hover:underline">
          Privacy Policy
        </Link>
        <span className="mx-2">•</span>{" "}
        <a
          href="https://discord.gg/z859AyVU3R"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white no-underline hover:underline"
        >
          Join our Discord
        </a>
      </footer>
    </div>
  );
};

export default ResetPassword;